/**
 * W4 cost-plan pure model — the number crunching and accrual-method metadata,
 * split out from the screen so it is unit-testable in isolation.
 *
 * Write-boundary note: this module only ever touches the PLAN columns of an
 * `AfeLineRow` (budget / accrual_method / unit / planned_qty / rate /
 * program_step). committed / accrued / actual are compute/Oxbo-Z owned and are
 * not even carried on `AfeLineRow`, so there is nothing here to accidentally
 * author. See oxbo-w-zsplit-and-endpoints.md §2.
 */
import type { AccrualMethod, AfeLineRow } from "@api/types";
import type { MessageKey } from "@i18n/messages";
import { asArray } from "@lib/asArray";

/** The four accrual methods the plan can declare, with i18n label + a token. */
export const ACCRUAL_METHODS: {
  value: AccrualMethod;
  labelKey: MessageKey;
  /** Semantic colour token used by the row badge (no raw hex). */
  tokenVar: string;
}[] = [
  { value: "avance_etapas", labelKey: "cost.accrual.avance_etapas", tokenVar: "--oxbo-z" },
  { value: "devengado", labelKey: "cost.accrual.devengado", tokenVar: "--good" },
  { value: "adjusted_cost", labelKey: "cost.accrual.adjusted_cost", tokenVar: "--oxbo-p" },
  { value: "straight_line", labelKey: "cost.accrual.straight_line", tokenVar: "--warn" },
];

const ACCRUAL_BY_VALUE = new Map(ACCRUAL_METHODS.map((m) => [m.value, m]));

/** Look up an accrual method's presentation metadata (label key + token). */
export function accrualMeta(method: AccrualMethod) {
  return ACCRUAL_BY_VALUE.get(method) ?? ACCRUAL_METHODS[0]!;
}

/**
 * A line's budget. SINGLE MODEL (was S2, ambiguous): budget is ALWAYS derived
 * as planned_qty × rate. qty and rate are the source of truth; budget is a pure
 * function of them, computed here and persisted by `withDerivedBudget` on save,
 * so a stored `budget` can never silently diverge from qty × rate. Null qty/rate
 * count as 0 (never NaN); the row's own `budget` field is not consulted.
 */
export function lineBudget(row: AfeLineRow): number {
  const qty = row.planned_qty ?? 0;
  const rate = row.rate ?? 0;
  const product = qty * rate;
  return Number.isFinite(product) ? product : 0;
}

/** Stamp `budget = qty × rate` onto a row so the persisted value matches display. */
export function withDerivedBudget(row: AfeLineRow): AfeLineRow {
  return { ...row, budget: lineBudget(row) };
}

/** Grand total of a set of AFE lines (Σ budget). Tolerates a non-array input. */
export function totalBudget(rows: AfeLineRow[]): number {
  return asArray<AfeLineRow>(rows).reduce((sum, r) => sum + lineBudget(r), 0);
}

/** A phase bucket key for a line: its explicit `phase`, else "—" (unassigned). */
export function phaseKey(row: AfeLineRow): string {
  return row.phase && row.phase.trim() ? row.phase : "—";
}

export interface PhaseTotal {
  phase: string;
  budget: number;
  count: number;
}

/**
 * Totals by phase, in first-seen order (stable for display). Each bucket carries
 * its summed budget and the line count so the UI can render bars + a footer.
 */
export function totalsByPhase(rows: AfeLineRow[]): PhaseTotal[] {
  const order: string[] = [];
  const map = new Map<string, PhaseTotal>();
  for (const r of asArray<AfeLineRow>(rows)) {
    const key = phaseKey(r);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { phase: key, budget: 0, count: 0 };
      map.set(key, bucket);
      order.push(key);
    }
    bucket.budget += lineBudget(r);
    bucket.count += 1;
  }
  return order.map((k) => map.get(k)!);
}

/**
 * Lines belonging to a given program step. `stepSeq === null` selects every
 * line (the "all steps" view); otherwise matches `program_step`.
 */
export function linesForStep(rows: AfeLineRow[], stepSeq: number | null): AfeLineRow[] {
  const list = asArray<AfeLineRow>(rows);
  if (stepSeq === null) return list;
  return list.filter((r) => r.program_step === stepSeq);
}
